"use client";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { User, Users, UserPlus } from "lucide-react";
import { CamundaUserService, CamundaTaskService, type CamundaTask, type CamundaUser } from "@/lib/camunda";
import { useState, useRef } from "react";
import { useClaimTask, useUnclaimTask, useReassignTask } from '@/stores/task-store';
import { useCanAssignTask, useCanCancelProcess } from '@/hooks/usePermissions';
import { CancelProcessButton } from "./cancel-process-button";
import { taskToast } from "@/lib/task-toast";
import { useAppRouter as useRouter } from "@/lib/navigation";

interface TaskAssignmentButtonProps {
  task: CamundaTask;
}

function getUserDisplayName(user: CamundaUser): string {
  const parts = [user.firstName, user.lastName].filter(Boolean);
  if (parts.length > 0) {
    return parts.join(' ');
  }

  return user.id;
}

function getAssigneeDisplayName(userId: string, users: CamundaUser[]): string {
  const user = users.find(candidate => candidate.id === userId);
  return user ? getUserDisplayName(user) : userId;
}

function appendErrorCode(description: string, eventId?: string): string {
  return eventId ? `${description}\nCódigo de error: ${eventId}` : description;
}

export function TaskAssignmentButton({ task }: TaskAssignmentButtonProps) {
  const currentUserId = CamundaUserService.getCurrentUserId();
  const { replace } = useRouter();

  const [candidateUsers, setCandidateUsers] = useState<CamundaUser[]>([]);
  const [open, setOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  const claimTask = useClaimTask();
  const unclaimTask = useUnclaimTask();
  const reassignTask = useReassignTask();

  const operationInProgress = useRef(false);

  const { hasPermission: hasTaskAssignPermission } = useCanAssignTask(
    task.processDefinitionId,
    task.processDefinitionKey
  );
  const { hasPermission: canDeleteProcess, loading: isCheckingDeletePermission } = useCanCancelProcess(
    task.processDefinitionId,
    task.processDefinitionKey
  );

  const canSeeCancel = !!task.processInstanceId && (isCheckingDeletePermission || canDeleteProcess);

  const displayName = task.assignee_fullname || '';

  const canUnclaimTask = task.assignee === currentUserId || hasTaskAssignPermission;

  const isDisabled = !currentUserId || !!task.assignee;
  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen || !task.assignee) {
      setCandidateUsers([]);
      return;
    }

    void CamundaTaskService.getTaskCandidateUsers(task.id)
      .then((users) => {
        setCandidateUsers(users);
      })
      .catch(() => {
        console.error('Error loading candidate users');
        setCandidateUsers([]);
        taskToast.error("No se pudieron cargar los usuarios candidatos", {
          description: "Reintenta abrir el menu o actualiza la tarea.",
        });
      });
  };

  const handleClaim = () => {
    if (!currentUserId || operationInProgress.current) return;
    operationInProgress.current = true;
    void claimTask(task.id)
      .then((result) => {
        if (result.ok) {
          taskToast.success("Tarea obtenida", {
            description: "Ya puedes editarla.",
          });
          return;
        }
        if (result.reason === 'conflict') {
          taskToast.warning("La tarea cambio en Camunda", {
            description: appendErrorCode(result.message, result.eventId),
          });
          return;
        }
        taskToast.error("No se pudo obtener la tarea", {
          description: appendErrorCode(result.message, result.eventId),
        });
      })
      .catch((error) => {
        console.error(error);
        taskToast.error("No se pudo obtener la tarea.");
      })
      .finally(() => { operationInProgress.current = false; });
  };

  const handleUnclaim = () => {
    if (operationInProgress.current) return;
    operationInProgress.current = true;
    void unclaimTask(task.id)
      .then((result) => {
        if (result.ok) {
          taskToast.success("Dejaste libre la tarea correctamente", {
            description: "Otra persona candidata puede tomarla.",
          });
          return;
        }
        if (result.reason === 'conflict') {
          taskToast.warning("La tarea cambio en Camunda", {
            description: appendErrorCode(result.message, result.eventId),
          });
          return;
        }
        taskToast.error("No se pudo liberar la tarea", {
          description: appendErrorCode(result.message, result.eventId),
        });
      })
      .catch((error) => {
        console.error(error);
        taskToast.error("No se pudo liberar la tarea.");
      })
      .finally(() => { operationInProgress.current = false; });
  };

  const handleReassign = (newAssigneeId: string) => {
    if (!newAssigneeId || newAssigneeId === task.assignee || operationInProgress.current) return;
    operationInProgress.current = true;
    void reassignTask(task.id, newAssigneeId)
      .then((result) => {
        if (result.ok) {
          setOpen(false);
          const assigneeName = getAssigneeDisplayName(newAssigneeId, candidateUsers);
          taskToast.success(`La tarea ahora es de ${assigneeName}`);
          if (newAssigneeId !== currentUserId) {
            replace('/task-list/tasks');
          }
          return;
        }
        if (result.reason === 'conflict') {
          taskToast.warning("La tarea cambio en Camunda", {
            description: appendErrorCode(result.message, result.eventId),
          });
          return;
        }
        taskToast.error("No se pudo reasignar la tarea", {
          description: appendErrorCode(result.message, result.eventId),
        });
      })
      .catch((error) => {
        console.error(error);
        taskToast.error("No se pudo reasignar la tarea.");
      })
      .finally(() => { operationInProgress.current = false; });
  };

  if (task.assignee) {
    return (
      <>
        <DropdownMenu open={open} onOpenChange={handleOpenChange}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-600 hover:bg-gray-50 border-0"
              disabled={!canUnclaimTask}
            >
              <User className="w-4 h-4 mr-1" />
              {displayName}
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" side="bottom">
            { }
            <DropdownMenuItem
              onClick={handleUnclaim}
              disabled={!canUnclaimTask}
              className={canUnclaimTask ? "!text-yellow-600 hover:!bg-yellow-50 focus:!text-yellow-600" : "text-gray-400"}
            >
              Liberar
            </DropdownMenuItem>

            {canSeeCancel && (
              <DropdownMenuItem
                onClick={() => setIsCancelDialogOpen(true)}
                disabled={isCheckingDeletePermission}
                className={
                  !isCheckingDeletePermission
                    ? "!text-red-600 hover:!bg-red-50 focus:!text-red-600"
                    : "text-gray-400 cursor-not-allowed"
                }
              >
                Cancelar
              </DropdownMenuItem>
            )}

            { }
            {candidateUsers.length > 0 && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger disabled={!hasTaskAssignPermission}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  {!hasTaskAssignPermission ? 'Sin permisos de reasignación' : 'Reasignar a...'}
                </DropdownMenuSubTrigger>
                {hasTaskAssignPermission && (
                  <DropdownMenuSubContent className="p-0">
                    <Command>
                      <CommandInput placeholder="Buscar usuario..." className="h-9" />
                      <CommandList>
                        <CommandEmpty>No se encontraron usuarios.</CommandEmpty>
                        <CommandGroup>
                          {candidateUsers.map(user => {
                            const userDisplayName = getUserDisplayName(user);
                            return (
                              <CommandItem
                                key={user.id}
                                value={userDisplayName}
                                onSelect={() => handleReassign(user.id)}
                                disabled={user.id === task.assignee}
                              >
                                <User className="w-4 h-4 mr-2" />
                                <div>
                                  <div className="font-medium">{userDisplayName}</div>
                                  {user.email && <div className="text-xs text-gray-500">{user.email}</div>}
                                </div>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </DropdownMenuSubContent>
                )}
              </DropdownMenuSub>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        { }
        {task.processInstanceId && (
          <CancelProcessButton
            task={task}
            isDialogOpen={isCancelDialogOpen}
            setIsDialogOpen={setIsCancelDialogOpen}
            canCancelProcess={canDeleteProcess}
          />
        )}
      </>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleClaim}
      disabled={isDisabled}
      className={`border-0 ${isDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50'}`}
      title={!currentUserId ? 'Usuario no identificado' : task.assignee ? 'Tarea ya asignada' : 'Reclamar esta tarea'}
    >
      <Users className="w-4 h-4 mr-1" />
      Obtener
    </Button>
  );
}
